import { useState, useEffect, useRef, useCallback } from 'react';
import { FileText, Plus, X, Upload, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatDate } from '@/lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Categories mirror /app/backend/documents.py DOCUMENT_CATEGORIES + CATEGORY_LABELS.
 * "other" is a special placeholder that requires a custom description on the request.
 */
const CATEGORY_OPTIONS = [
  { value: 'police_accident_report', label: 'Police / Accident Report' },
  { value: 'er_urgent_care_records', label: 'ER / Urgent Care Records' },
  { value: 'medical_records', label: 'Medical Records' },
  { value: 'medical_bills', label: 'Medical Bills' },
  { value: 'insurance_declaration_page', label: 'Insurance Declaration Page' },
  { value: 'insurance_claim_information', label: 'Insurance Claim Information' },
  { value: 'legal_complaint', label: 'Legal Complaint' },
  { value: 'demand_letter', label: 'Demand Letter' },
  { value: 'attorney_representation_letter', label: 'Attorney Representation Letter' },
  { value: 'settlement_offer_correspondence', label: 'Settlement Offer / Correspondence' },
  { value: 'photos_videos', label: 'Photos / Videos' },
  { value: 'other_supporting_documents', label: 'Other Supporting Documents' },
  { value: 'other', label: 'Other (custom description)' },
];

const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
const EXT_TO_MIME = {
  '.pdf': 'application/pdf',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

const statusBadge = (status) => {
  const map = {
    requested: { label: 'Requested', bg: '#fef3c7', color: '#92400e', Icon: Clock },
    uploaded: { label: 'Uploaded', bg: '#dbeafe', color: '#1e40af', Icon: Upload },
    fulfilled: { label: 'Fulfilled', bg: '#dcfce7', color: '#166534', Icon: CheckCircle2 },
  };
  const s = map[status] || map.requested;
  const Icon = s.Icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600, background: s.bg, color: s.color }} data-testid={`docreq-status-${status}`}>
      <Icon size={11} /> {s.label}
    </span>
  );
};

const fmtDate = (v) => formatDate(v) || '—';

/**
 * DocumentRequestsCard
 *
 * @param {string} applicationId
 * @param {'funder'|'fulfill'|'view'} mode  Determines which controls are shown.
 *   - funder   : shows "Request Documents" button + full table
 *   - fulfill  : shows outstanding requests with per-row Upload button (client/lawyer/paralegal)
 *   - view     : read-only (admin)
 * @param {() => object} getAuthHeaders
 * @param {() => void} [onChanged] optional callback fired after mutation for parent refresh
 */
const DocumentRequestsCard = ({ applicationId, mode = 'view', getAuthHeaders, onChanged }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/applications/${applicationId}/document-requests`, {
        credentials: 'include', headers: getAuthHeaders(),
      });
      if (r.ok) {
        const d = await r.json();
        setRequests(d.requests || []);
      }
    } catch (e) {
      console.error('Failed to load document requests', e);
    } finally {
      setLoading(false);
    }
  }, [applicationId, getAuthHeaders]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const outstanding = requests.filter(r => r.status !== 'fulfilled');
  const fulfilled = requests.filter(r => r.status === 'fulfilled');

  return (
    <div className="mcl-card mb-3" data-testid="document-requests-card">
      <div className="mcl-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 className="mcl-card-header-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <FileText size={16} /> {mode === 'funder' ? 'Document Requests' : 'Requested Documents'}
        </h3>
        {mode === 'funder' && (
          <button
            type="button"
            className="mcl-btn mcl-btn-sm mcl-btn-primary"
            onClick={() => setShowCreate(true)}
            data-testid="open-request-documents-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            <Plus size={14} /> Request Documents
          </button>
        )}
      </div>
      <div className="mcl-card-body">
        {loading ? (
          <div style={{ color: 'var(--mcl-text-muted)', fontSize: '0.85rem' }}>Loading…</div>
        ) : requests.length === 0 ? (
          <div style={{ color: 'var(--mcl-text-muted)', fontSize: '0.85rem', padding: '0.75rem 0' }} data-testid="docreq-empty">
            {mode === 'funder'
              ? 'No document requests yet. Click Request Documents to ask for supporting records.'
              : 'No documents have been requested for this application.'}
          </div>
        ) : mode === 'funder' ? (
          <FunderRequestsTable
            requests={requests}
            applicationId={applicationId}
            getAuthHeaders={getAuthHeaders}
            onChanged={() => { fetchRequests(); onChanged?.(); }}
          />
        ) : (
          <>
            {outstanding.length > 0 && (
              <div>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--mcl-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Outstanding ({outstanding.length})</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {outstanding.map((r) => (
                    <OutstandingRow
                      key={r.request_id}
                      req={r}
                      applicationId={applicationId}
                      canUpload={mode === 'fulfill'}
                      getAuthHeaders={getAuthHeaders}
                      onUploaded={() => { fetchRequests(); onChanged?.(); }}
                    />
                  ))}
                </div>
              </div>
            )}
            {fulfilled.length > 0 && (
              <div style={{ marginTop: outstanding.length > 0 ? '1rem' : 0 }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--mcl-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Fulfilled ({fulfilled.length})</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {fulfilled.map((r) => (
                    <FulfilledRow key={r.request_id} req={r} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showCreate && (
        <CreateRequestModal
          applicationId={applicationId}
          existing={requests}
          getAuthHeaders={getAuthHeaders}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchRequests(); onChanged?.(); }}
        />
      )}
    </div>
  );
};

/* --------------------------- Funder table --------------------------- */
const FunderRequestsTable = ({ requests, applicationId, getAuthHeaders, onChanged }) => {
  const handleCancel = async (req) => {
    if (!window.confirm(`Cancel request for "${req.display_label}"?`)) return;
    try {
      const r = await fetch(`${API}/document-requests/${req.request_id}`, {
        method: 'DELETE', credentials: 'include', headers: getAuthHeaders(),
      });
      if (r.ok) {
        toast.success('Request canceled');
        onChanged();
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Failed to cancel');
      }
    } catch (e) {
      toast.error('Failed to cancel');
    }
  };

  return (
    <div className="mcl-table-wrapper">
      <table className="mcl-table" data-testid="funder-doc-requests-table">
        <thead>
          <tr>
            <th>Document</th>
            <th>Requested</th>
            <th>Status</th>
            <th>Uploaded By</th>
            <th>Uploaded</th>
            <th>Notes</th>
            <th>File</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {requests.map((r) => (
            <tr key={r.request_id} data-testid={`docreq-row-${r.request_id}`}>
              <td>
                <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>{r.display_label}</div>
              </td>
              <td style={{ fontSize: '0.82rem', color: 'var(--mcl-text-muted)' }}>{fmtDate(r.requested_at)}</td>
              <td>{statusBadge(r.status)}</td>
              <td style={{ fontSize: '0.82rem' }}>{r.uploaded_by_name || <span style={{ color: 'var(--mcl-text-light)' }}>—</span>}</td>
              <td style={{ fontSize: '0.82rem', color: 'var(--mcl-text-muted)' }}>{fmtDate(r.uploaded_at)}</td>
              <td style={{ fontSize: '0.82rem', maxWidth: 220, whiteSpace: 'normal' }}>{r.notes || <span style={{ color: 'var(--mcl-text-light)' }}>—</span>}</td>
              <td>
                {r.document_id ? (
                  <DocDownloadLink documentId={r.document_id} getAuthHeaders={getAuthHeaders} />
                ) : (
                  <span style={{ color: 'var(--mcl-text-light)' }}>—</span>
                )}
              </td>
              <td style={{ textAlign: 'right' }}>
                {/* is_mine is false on a peer funder's request — the API masks
                    its owner and DELETE would 403, so offer no button. */}
                {r.status !== 'fulfilled' && r.is_mine !== false && (
                  <button
                    type="button"
                    className="mcl-action-btn"
                    onClick={() => handleCancel(r)}
                    title="Cancel request"
                    data-testid={`cancel-docreq-${r.request_id}`}
                    style={{ color: 'var(--mcl-danger)' }}
                  >
                    <X size={14} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DocDownloadLink = ({ documentId, getAuthHeaders }) => {
  const [loading, setLoading] = useState(false);
  const openIt = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/documents/${documentId}/download-url`, {
        credentials: 'include', headers: getAuthHeaders(),
      });
      if (r.ok) {
        const d = await r.json();
        window.open(d.download_url, '_blank', 'noopener');
      } else {
        toast.error('Failed to open document');
      }
    } catch {
      toast.error('Failed to open document');
    } finally {
      setLoading(false);
    }
  };
  return (
    <button type="button" className="mcl-btn mcl-btn-sm mcl-btn-outline" onClick={openIt} disabled={loading} data-testid={`open-docreq-file-${documentId}`}>
      {loading ? '…' : 'View'}
    </button>
  );
};

/* -------------------- Outstanding row (client/lawyer/paralegal) -------------------- */
const OutstandingRow = ({ req, applicationId, canUpload, getAuthHeaders, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const targetCategory = req.document_category === 'other'
    ? 'other_supporting_documents'
    : req.document_category;

  const handlePick = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;

    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(`File type not allowed. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large (max 25MB).');
      return;
    }
    const contentType = file.type && file.type !== 'application/octet-stream'
      ? file.type
      : EXT_TO_MIME[ext];

    setUploading(true);
    try {
      // 1. presigned upload URL — tag with document_request_id
      const urlRes = await fetch(`${API}/applications/${applicationId}/documents/upload-url`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          filename: file.name,
          content_type: contentType,
          file_size: file.size,
          category: targetCategory,
          document_request_id: req.request_id,
        }),
      });
      if (!urlRes.ok) {
        const err = await urlRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to start upload');
      }
      const { document_id, upload_url } = await urlRes.json();

      // 2. PUT the file to S3
      const putRes = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
      });
      if (!putRes.ok) throw new Error('Failed to upload to storage');

      // 3. Confirm
      const confirmRes = await fetch(`${API}/documents/${document_id}/confirm-upload`, {
        method: 'POST', credentials: 'include', headers: getAuthHeaders(),
      });
      if (!confirmRes.ok) throw new Error('Failed to confirm upload');

      toast.success('Document uploaded — request fulfilled');
      onUploaded();
    } catch (e) {
      console.error('Fulfill upload failed:', e);
      toast.error(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '0.65rem 0.85rem', border: '1px solid var(--mcl-border-light, #e2e8f0)', borderRadius: 8, background: '#fff' }} data-testid={`outstanding-row-${req.request_id}`}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)', fontSize: '0.9rem' }}>{req.display_label}</div>
          {statusBadge(req.status)}
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--mcl-text-muted)' }}>
          Requested by {req.funder_company || req.funder_name} · {fmtDate(req.requested_at)}
        </div>
        {req.notes && (
          <div style={{ fontSize: '0.82rem', color: 'var(--mcl-text-body)', marginTop: 4, padding: '4px 8px', background: '#f8fafc', borderLeft: '3px solid #cbd5e1', borderRadius: 4 }}>
            {req.notes}
          </div>
        )}
      </div>
      {canUpload && (
        <>
          <input
            ref={fileRef}
            type="file"
            style={{ display: 'none' }}
            accept={ALLOWED_EXTENSIONS.join(',')}
            onChange={handleFile}
            disabled={uploading}
            data-testid={`fulfill-file-input-${req.request_id}`}
          />
          <button
            type="button"
            className="mcl-btn mcl-btn-sm mcl-btn-primary"
            onClick={handlePick}
            disabled={uploading}
            data-testid={`upload-docreq-${req.request_id}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0 }}
          >
            <Upload size={13} /> {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </>
      )}
    </div>
  );
};

const FulfilledRow = ({ req }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.8rem', background: '#f8fafc', borderRadius: 6, fontSize: '0.85rem' }} data-testid={`fulfilled-row-${req.request_id}`}>
    <div>
      <span style={{ fontWeight: 600 }}>{req.display_label}</span>
      <span style={{ color: 'var(--mcl-text-muted)', marginLeft: 8 }}>
        · uploaded by {req.uploaded_by_name || '—'} on {fmtDate(req.uploaded_at)}
      </span>
    </div>
    {statusBadge(req.status)}
  </div>
);

/* --------------------------- Create modal --------------------------- */
const CreateRequestModal = ({ applicationId, existing, getAuthHeaders, onClose, onCreated }) => {
  // Categories currently open (requested/uploaded) — pre-fill so funder sees which ones are blocked
  const openKeys = new Set();
  existing.forEach(r => {
    if (r.status !== 'fulfilled') {
      const desc = (r.other_description || '').trim().toLowerCase() || '';
      openKeys.add(`${r.document_category}::${desc}`);
    }
  });

  const [items, setItems] = useState([
    { document_category: '', other_description: '', notes: '' },
  ]);
  const [submitting, setSubmitting] = useState(false);

  const setItem = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };
  const addRow = () => setItems(prev => [...prev, { document_category: '', other_description: '', notes: '' }]);
  const removeRow = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    // Basic validation
    const cleaned = [];
    for (const it of items) {
      if (!it.document_category) continue;
      if (it.document_category === 'other' && !(it.other_description || '').trim()) {
        toast.error('Please describe the "Other" document');
        return;
      }
      const desc = (it.other_description || '').trim().toLowerCase();
      const key = `${it.document_category}::${it.document_category === 'other' ? desc : ''}`;
      if (openKeys.has(key)) {
        toast.error(`Duplicate open request for ${it.document_category === 'other' ? `"${it.other_description}"` : it.document_category}`);
        return;
      }
      cleaned.push({
        document_category: it.document_category,
        other_description: it.document_category === 'other' ? it.other_description.trim() : null,
        notes: (it.notes || '').trim() || null,
      });
    }
    if (cleaned.length === 0) {
      toast.error('Please select at least one document');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/applications/${applicationId}/document-requests`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ items: cleaned }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed to create requests');
      if ((data.created?.length || 0) === 0) {
        toast.error(data.duplicates?.length ? 'All requested items already exist' : 'No requests created');
      } else {
        const n = data.created.length;
        const dup = data.duplicates?.length || 0;
        toast.success(`Created ${n} request${n === 1 ? '' : 's'}${dup ? ` (${dup} skipped as duplicates)` : ''}`);
        onCreated();
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent style={{ maxWidth: 680, maxHeight: '88vh', display: 'flex', flexDirection: 'column' }} data-testid="create-doc-request-modal">
        <DialogHeader>
          <DialogTitle>Request Documents</DialogTitle>
          <DialogDescription>Select one or more supporting documents to request from the client, lawyer, and their team.</DialogDescription>
        </DialogHeader>
        <div style={{ overflowY: 'auto', flex: 1, paddingRight: '0.35rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {items.map((it, idx) => (
            <div key={idx} style={{ border: '1px solid var(--mcl-border-light, #e2e8f0)', borderRadius: 8, padding: '0.75rem', background: '#fafbff' }} data-testid={`docreq-item-${idx}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <strong style={{ fontSize: '0.82rem', color: 'var(--mcl-text-heading)' }}>Document #{idx + 1}</strong>
                {items.length > 1 && (
                  <button type="button" className="mcl-action-btn" onClick={() => removeRow(idx)} data-testid={`remove-docreq-item-${idx}`} style={{ color: 'var(--mcl-danger)' }}>
                    <X size={13} />
                  </button>
                )}
              </div>
              <div className="mcl-form-group" style={{ marginBottom: '0.5rem' }}>
                <label className="mcl-form-label">Document *</label>
                <select
                  className="mcl-form-input"
                  value={it.document_category}
                  onChange={(e) => setItem(idx, 'document_category', e.target.value)}
                  data-testid={`docreq-item-category-${idx}`}
                >
                  <option value="">Select a document…</option>
                  {CATEGORY_OPTIONS.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              {it.document_category === 'other' && (
                <div className="mcl-form-group" style={{ marginBottom: '0.5rem' }}>
                  <label className="mcl-form-label">Custom description *</label>
                  <input
                    type="text"
                    className="mcl-form-input"
                    value={it.other_description}
                    onChange={(e) => setItem(idx, 'other_description', e.target.value)}
                    placeholder="e.g. Photo of vehicle title"
                    data-testid={`docreq-item-other-${idx}`}
                  />
                </div>
              )}
              <div className="mcl-form-group" style={{ marginBottom: 0 }}>
                <label className="mcl-form-label">Notes (optional)</label>
                <textarea
                  className="mcl-form-input"
                  rows={2}
                  value={it.notes}
                  onChange={(e) => setItem(idx, 'notes', e.target.value)}
                  placeholder="Add any instructions or context…"
                  data-testid={`docreq-item-notes-${idx}`}
                  style={{ resize: 'vertical' }}
                />
              </div>
            </div>
          ))}
          <button
            type="button"
            className="mcl-btn mcl-btn-outline"
            onClick={addRow}
            data-testid="add-docreq-item"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, alignSelf: 'flex-start' }}
          >
            <Plus size={14} /> Add another document
          </button>
        </div>
        <DialogFooter style={{ paddingTop: '0.75rem', borderTop: '1px solid var(--mcl-border-light, #e2e8f0)' }}>
          <button type="button" className="mcl-btn mcl-btn-outline" onClick={onClose} disabled={submitting} data-testid="cancel-docreq-btn">Cancel</button>
          <button type="button" className="mcl-btn mcl-btn-primary" onClick={submit} disabled={submitting} data-testid="submit-docreq-btn">
            {submitting ? 'Requesting…' : 'Send Request'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentRequestsCard;
